<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import SocialProviders from '@/components/SocialProviders.vue'
import { useRealmSettings } from '@/stores/useRealmSettings'
import { customElementPropsKey } from '@/types/customElementProps.types'
import { storeToRefs } from 'pinia'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const templateProps = inject(customElementPropsKey)
const { t } = useI18n()

const { realmSettings } = storeToRefs(useRealmSettings())

const isPasswordAuthEnabled = computed(
  () => !!realmSettings.value?.auth.password_auth_enabled,
)
const hasSocialProviders = computed(
  () => templateProps?.supportedSocialProviders?.length,
)
const username = ref(templateProps?.loginUsername)
const rememberMe = ref('on')
const showForm = ref(!hasSocialProviders.value)

// If the settings define a password_auth_hint_domain, we show that in the username field when a password error occurs.
const usernameError = computed(() => {
  if (
    ['error', 'warning'].includes(templateProps?.genericMessageType ?? '') &&
    templateProps?.genericMessage &&
    realmSettings.value?.auth.password_auth_hint_domain
  ) {
    return t('vue.error.email.domain', {
      domain: realmSettings.value.auth.password_auth_hint_domain,
    })
  } else {
    return undefined
  }
})
</script>

<template>
  <div>
    <PageTitle :title="$t('on-boarding.sign-in.screen.title')" />

    <SocialProviders v-if="hasSocialProviders" />

    <div
      v-if="isPasswordAuthEnabled && hasSocialProviders"
      class="d-flex align-center my-6 gap-2 text-medium-emphasis text-body-2"
    >
      <hr class="flex-grow-1" />
      <span>{{ $t('authentication.provider.separator.label') }}</span>
      <hr class="flex-grow-1" />
    </div>

    <div v-if="!showForm && isPasswordAuthEnabled">
      <v-btn
        block
        data-cy="login-with-email"
        class="white-background-button"
        @click="showForm = true"
      >
        {{ $t('authentication.login-email.btn')}}
      </v-btn>
    </div>
    <form
      v-if="showForm && isPasswordAuthEnabled"
      :action="templateProps?.formActionUrl"
      method="post"
      novalidate
      class="my-6"
    >
      <div>
        <v-input>
          <v-text-field
            v-model="username"
            type="email"
            autofocus
            name="username"
            data-cy="username"
            autocomplete="username"
            :label="$t('authentication.email.label')"
            :rules="[v => !!v || $t('error.field.required')]"
            :error-messages="usernameError ? [$t(usernameError)] : undefined"
            :error="!!usernameError"
          />
        </v-input>

        <v-input>
          <v-text-field
            data-cy="password"
            type="password"
            name="password"
            autocomplete="current-password"
            :label="$t('authentication.password.label')"
          />
        </v-input>

        <div
          v-if="
            templateProps?.rememberMeEnabled ||
            templateProps?.resetPasswordEnabled
          "
          class="d-flex justify-space-between flex-wrap align-center mt-n6 mb-4"
        >
          <v-checkbox
            :label="$t('authentication.remember-me')"
            hide-details
            name="rememberMe"
            true-value="on"
            v-model="rememberMe"
            v-if="templateProps?.rememberMeEnabled"
          />
          <a
            v-if="templateProps?.resetPasswordEnabled"
            class="text-primary text-caption text-decoration-underline"
            :href="templateProps?.resetPasswordUrl"
            >{{ $t('authentication.reset-password.button') }}</a
          >
        </div>

        <v-input>
          <v-btn
            block
            type="submit"
            data-cy="login-submit"
          >{{
            $t('authentication.submit.button')
          }}</v-btn>
        </v-input>
      </div>
    </form>
  </div>
</template>
<style lang="css" scoped>
.white-background-button {
  background-color: #F7F7F8 !important;
  color: #432ca0 !important;
  border: thin solid #432ca0 !important;
}
</style>
